import { useState, useMemo } from "react";
import { ArrowUpDown, ExternalLink, Circle } from "lucide-react";
import type { Modem } from "../types/types";

interface ModemTableProps {
  modems: Modem[];
  onModemClick: (modemId: string) => void;
}

type SortKey = "name" | "unit_name" | "operational_status" | "last_update";
type SortOrder = "asc" | "desc";

export function ModemTable({ modems, onModemClick }: ModemTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const toggleRow = (id: string) => {
    setExpandedRow(expandedRow === id ? null : id);
  };
  const sortedModems = useMemo(() => {
  return [...modems].sort((a, b) => {
    let comparison = 0;

    if (sortKey === "last_update") {
      const aTime = new Date(a.last_update).getTime();
      const bTime = new Date(b.last_update).getTime();

      comparison = aTime - bTime;
    } else {
      const aVal = a[sortKey];
      const bVal = b[sortKey];

      comparison = String(aVal).localeCompare(String(bVal));
    }

    return sortOrder === "asc" ? comparison : -comparison;
  });
  }, [modems, sortKey, sortOrder]);

  const paginatedModems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedModems.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedModems, currentPage]);

  const totalPages = Math.max(
    1,
    Math.ceil(modems.length / itemsPerPage)
  );

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "online":
        return "text-green-500";
      case "alarm":
        return "text-red-500";
      case "offline":
        return "text-gray-500";
      case "invalid-data":
        return "text-orange-500";
      default:
        return "text-amber-500";
    }
  };

  const formatLastUpdate = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (timestamp == "N/A") return "N/A";
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-slate-900  border border-slate-800">
      <div className="overflow-x-auto">
        <table className="w-full hidden sm:table">
          <thead>
            <tr className="border-b border-slate-800">
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => handleSort("name")}
                  className="flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-white transition-colors"
                >
                  Unit Name
                  <ArrowUpDown className="w-4 h-4" />
                </button>
              </th>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => handleSort("unit_name")}
                  className="flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-white transition-colors"
                >
                  UnitId
                  <ArrowUpDown className="w-4 h-4" />
                </button>
              </th>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => handleSort("operational_status")}
                  className="flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-white transition-colors"
                >
                  Status
                  <ArrowUpDown className="w-4 h-4" />
                </button>
              </th>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => handleSort("last_update")}
                  className="flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-white transition-colors"
                >
                  Last Update
                  <ArrowUpDown className="w-4 h-4" />
                </button>
              </th>
              <th className="px-6 py-4 text-left">
                <span className="text-sm font-semibold text-slate-300">
                  Actions
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedModems.map((modem) => (
              <tr
                key={modem.id}
                className="border-b border-slate-800 hover:bg-slate-850 transition-colors"
              >
                <td className="px-6 py-4">
                  <span className="text-sm font-medium text-white">
                    {modem.name}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-slate-300">
                    {modem.unit_name}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Circle
                      className={`w-3 h-3 fill-current ${getStatusColor(
                        modem.operational_status
                      )}`}
                    />
                    <span
                      className={`text-sm font-medium capitalize ${getStatusColor(
                        modem.operational_status
                      )}`}
                    >
                      {modem.operational_status === "offline"
                        ? "Missed Com"
                        : modem.operational_status}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-slate-400">
                    {formatLastUpdate(modem.last_update)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => onModemClick(modem.id)}
                    className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    View Details
                    <ExternalLink className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="sm:hidden flex flex-col gap-2 p-2 h-[500px] overflow-y-auto">
          {paginatedModems.map((modem) => (
            <div key={modem.id} className="bg-slate-800 p-4">
              <div
                className="flex items-center cursor-pointer"
                onClick={() => toggleRow(modem.id)}
              >
                <ArrowUpDown
                  className={`w-4 h-4 mr-2 transition-transform ${
                    expandedRow === modem.id ? "rotate-180" : ""
                  }`}
                />
                <span className="text-white font-medium">{modem.name}</span>
              </div>

              {expandedRow === modem.id && (
                <div className="mt-2 text-sm text-slate-300 flex flex-col gap-1">
                  <div>
                    <span className="font-semibold text-slate-400">
                      UnitId:{" "}
                    </span>
                    {modem.unit_name}
                  </div>
                  <div className="flex items-center gap-2">
                    <Circle
                      className={`w-3 h-3 fill-current ${getStatusColor(
                        modem.operational_status
                      )}`}
                    />
                    <span
                      className={`capitalize ${getStatusColor(
                        modem.operational_status
                      )}`}
                    >
                      {modem.operational_status === "offline"
                        ? "Missed Com"
                        : modem.operational_status}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-slate-400">
                      Last Update:{" "}
                    </span>
                    {formatLastUpdate(modem.last_update)}
                  </div>
                  <button
                    onClick={() => onModemClick(modem.id)}
                    className="flex items-center gap-1 text-blue-400 hover:text-blue-300 mt-1"
                  >
                    View Details <ExternalLink className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800">
        <div className="text-sm text-slate-400">
          Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
          {Math.min(currentPage * itemsPerPage, modems.length)} of{" "}
          {modems.length} modems
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 text-sm bg-slate-800 text-white  hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-slate-400">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 text-sm bg-slate-800 text-white  hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
